import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { generatePath, useHistory } from 'react-router-dom'
import { ItemId, RenderItemParams } from 'tree-dnd'
import { FavoriteId } from 'api/clients/FavoriteClient'
import { FavoriteService } from 'api/services/FavoriteService'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { handbookLink } from 'components/Handbook2_0/util'
import { useCurrentArticleId } from 'hooks/useCurrentArticleId'
import { ALL_FILES_PATH, GROUP_PATH } from 'paths'
import { AlmanacFile, BaseEmoji, SetState } from 'types'
import { getAllDocsPath, getArticlePath, getDesignPath, getFolderPath } from 'utils/urlUtils'
import { TreeDataItem, TreeItemType } from '../types'
import Styles from './TreeRow.module.scss'
import { MoreThick } from 'almanac-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useHandbookRouteMatch } from 'components/Handbook2_0/hooks/useHandbookRouteMatch'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { FolderMenuContainer } from 'components/FolderMenu/FolderMenuContainer'
import { ContextMenuTrigger } from 'almanac-ui/ContextMenuTrigger/ContextMenuTrigger'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { FolderPreloader } from 'components/FolderPreloader/FolderPreloader'
import { useUpdateAlmanacFile } from 'hooks/queries/almanacFiles/useUpdateAlmanacFile'
import { DocMenuContainer } from 'components/DocMenu/DocMenuContainer'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useCloseLeftSidebar } from 'hooks/useCloseLeftSidebar'
import cx from 'classnames'
import { RenameInput } from '../RenameInput/RenameInput'
import { NavItem } from '../../NavItem'
import { FileIcon } from 'screens/Finder/components/FileIcon/FileIcon'
import { useFinderRefresh } from 'screens/Finder/hooks/useFinderRefresh'
import { NavRightClickMenuContainer } from 'components/NavRightClickMenu/NavRightClickMenuContainer'
import { noop } from 'lodash'
import { ProjectMenuDrawer } from 'blaze-components/ProjectMenu/ProjectMenuDrawer'
import { DocMenuDrawer } from 'blaze-components/MobileWriterTopBar/DocMenuDrawer'
import { MoreDots } from 'blaze-ui/icons'
import { useRootStore } from 'stores/RootStore'
import { IconButton } from 'components/IconButton/IconButton'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface TreeRowProps extends RenderItemParams {
  selectedRows: ItemId | undefined
  setSelectedRows: SetState<ItemId | undefined>
  isFavorite?: boolean
  isLast?: boolean
  hasMore?: boolean
  loadMore?: () => Promise<any>
  onRemoveItem?: (itemId: ItemId) => void
  updateItem: (itemId: ItemId, data: TreeDataItem) => void
  activeFolder?: AlmanacFile | null
}

export const pathForTreeItem = (treeItem: TreeDataItem) => {
  switch (treeItem.type) {
    case TreeItemType.Article:
      return getArticlePath({ slug: treeItem.slug || '' })
    case TreeItemType.Design:
      return getDesignPath({ slug: treeItem.id || '' })
    case TreeItemType.Channel:
      return generatePath(GROUP_PATH, { id: treeItem.id, workspaceId: treeItem.real_workspace_id })
    case TreeItemType.Project:
      if (treeItem.handbook?.discarded_at) {
        return generatePath(ALL_FILES_PATH, {
          workspaceId: treeItem.real_workspace_id,
        })
      }
      if (treeItem.handbook) {
        return handbookLink(treeItem.handbook, undefined, true)
      }
      if (treeItem.slug) {
        return getFolderPath({ folderSlug: treeItem.slug })
      }
      return generatePath(ALL_FILES_PATH, {
        location: treeItem.id,
        workspaceId: treeItem.real_workspace_id,
      })
    case TreeItemType.Handbook:
      return handbookLink({ seo_string: treeItem.slug || '' })
    default:
      return ''
  }
}

export const TreeRow = observer(function TreeRow({
  item,
  provided,
  selectedRows,
  setSelectedRows,
  isFavorite = false,
  isLast,
  loadMore,
  hasMore,
  onRemoveItem,
  updateItem,
  activeFolder,
}: TreeRowProps) {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const file = item.data as TreeDataItem
  const isFolder = file.type === TreeItemType.Project
  const isArticle = file.type === TreeItemType.Article
  const { handbookKey, docKey } = useHandbookRouteMatch()
  const [renaming, setRenaming] = useState(false)
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const history = useHistory()
  const currentArticleId = useCurrentArticleId()
  const { isMobile, isMobileMd } = useResponsiveDesign()
  const { mutateAsync } = useUpdateAlmanacFile({ key: file.id })
  const { closeLeftSidebar } = useCloseLeftSidebar()
  const refreshFinder = useFinderRefresh()
  const { drawersStore } = useRootStore()
  const docMenuHiddenKeys = [
    'toggleVersionControl',
    'switchView',
    'readHistory',
    'toggleFocus',
    'export',
    'readInsights',
    'publish',
  ]
  const isActiveFolderSlugKey = file.slug?.split('-').pop()
  const isActive =
    (!!handbookKey && (handbookKey === file.handbook?.key || handbookKey === isActiveFolderSlugKey) && !docKey) ||
    (!!docKey && file.id === docKey)

  const rightClickMenuVisible = useMemo(() => selectedRows === item.id, [selectedRows, item.id])
  const setRightClickMenuVisible = useCallback(
    (visible: boolean) => {
      visible ? setSelectedRows(item.id) : setSelectedRows(undefined)
    },
    [setSelectedRows, item.id],
  )

  const resetQueries = () => {
    queryClient.invalidateQueries(['workspace', file.real_workspace_id, 'files'])
    refreshFinder()
  }

  const isRootLevelLeaf = file.parent_id === '0' || !file.parent_id

  const onDeleteRequest = (removedFiles: TreeDataItem[]) => {
    removedFiles.forEach((file) => {
      onRemoveItem && onRemoveItem(file.id)

      if (isFavorite) {
        FavoriteService.deleteFavorite(parseInt(item.id as string) as FavoriteId, dispatch).finally(() => {
          if (!!currentArticleId) history.push(getAllDocsPath(file.real_workspace_id))

          resetQueries()
        })
      }
    })
  }

  const onRename = (file: TreeDataItem, newName: string) => {
    updateItem(item.id, { ...file, title: newName })

    const response = mutateAsync({ title: newName })

    response
      .then(() => {
        resetQueries()
      })
      .catch((error) => {
        let errorMessage = 'Something went wrong. Please try again.'
        if (error instanceof Error) {
          errorMessage = error.message
        }
        showToastMessage(errorMessage, 'error')
      })
  }

  const onMoveRequest = () => {
    resetQueries()
  }

  const onCopyRequest = () => {
    resetQueries()
  }

  const handleRowClick = () => {
    if (rightClickMenuVisible) {
      setRightClickMenuVisible(false)
    }

    if (isMobile) {
      closeLeftSidebar()
    }
  }

  const handleRightClick = () => {
    setRightClickMenuVisible(!rightClickMenuVisible)
  }

  const updateEmoji = async (emoji: BaseEmoji | null) => {
    const newEmoji = emoji?.native
    const currentEmoji = item.data.header_emoji
    // Optimistic update
    updateItem(item.id, { ...item.data, header_emoji: newEmoji })

    try {
      await mutateAsync({ emoji: newEmoji })
    } catch {
      // Rollback
      updateItem(item.id, { ...item.data, header_emoji: currentEmoji })
    }
  }

  const folderMenuPopover = (close: () => void) => (
    <FolderMenuContainer
      id={file.id}
      dismiss={close}
      onAction={(key) => {
        if (key === 'rename') {
          setRenaming(true)
        }
        if (key === 'move') {
          return
        }
        close()
      }}
    />
  )

  const docMenuPopover = (close: () => void) => (
    <DocMenuContainer
      id={file.id}
      hiddenKeys={new Set(docMenuHiddenKeys)}
      dismiss={close}
      onAction={(key) => {
        if (key === 'rename') {
          setRenaming(true)
        }
        close()
      }}
    />
  )

  const desktopFileIconSize = isSystemRedesignEnabled ? 'md-ap' : 'md'
  const fileIconSize = isMobile ? 'lg' : desktopFileIconSize

  return (
    <>
      <div
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        ref={provided?.innerRef}
        onContextMenu={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleRightClick()
        }}
        data-testid="treeRow"
      >
        {renaming ? (
          <div className={Styles.renameWrapper}>
            <RenameInput
              onRename={(file, newName) => onRename(file as TreeDataItem, newName)}
              setShowRename={setRenaming}
              file={file as AlmanacFile}
            />
          </div>
        ) : (
          <ConditionalWrapper
            condition={isFolder}
            wrapper={(children) => <FolderPreloader fileId={file.id}>{children}</FolderPreloader>}
          >
            <ConditionalWrapper
              condition={!isMobile && (isFolder || isArticle)}
              wrapper={(children) => (
                <ContextMenuTrigger placement="bottom-start">
                  <div>{children}</div>
                  {(close) => {
                    if (isFolder) return folderMenuPopover(close)
                    if (isArticle) return docMenuPopover(close)
                  }}
                </ContextMenuTrigger>
              )}
              elseWrapper={(children) => (
                <ContextMenuTrigger placement="bottom-start">
                  {children}
                  {(close) => (
                    <NavRightClickMenuContainer
                      visible={rightClickMenuVisible}
                      setVisible={setRightClickMenuVisible}
                      selectedFiles={[file as AlmanacFile]}
                      showShare
                      showRename
                      showMove
                      showDelete
                      currentFolder={isFolder ? file : undefined}
                      initialFolder={file.ancestor_projects?.slice(-1)?.pop()}
                      showDuplicate
                      onDeleteRequest={(removedFiles) => onDeleteRequest(removedFiles as TreeDataItem[])}
                      setShowRename={noop}
                      onMoveRequest={onMoveRequest}
                      onCopyRequest={onCopyRequest}
                      disableEditOnlyButtons={!file.has_edit_access || !!file.mention_id}
                      onAction={(key) => {
                        if (key === 'rename') {
                          setRenaming(true)
                        }
                        close()
                      }}
                    />
                  )}
                </ContextMenuTrigger>
              )}
            >
              <NavItem.Root
                size={isMobile ? 'lg' : 'md'}
                to={pathForTreeItem(file)}
                onPress={handleRowClick}
                isActive={isActive}
                classNames={cx(Styles.row, { [Styles.applyBorder]: !isLast })}
              >
                <NavItem.Emoji
                  isEditable={file.has_edit_access && file.type !== 'folder'}
                  onChange={updateEmoji}
                  fileKey={file.id}
                  withChannels={file.type === TreeItemType.Article}
                >
                  <FileIcon
                    className={Styles.fileIcon}
                    file={file as AlmanacFile}
                    isExpanded={activeFolder?.id === file.id}
                    size={fileIconSize}
                    color="var(--dark-60)"
                  />
                </NavItem.Emoji>
                <NavItem.Label label={file.title} />
                <NavItem.ControlGroup>
                  {isFolder || isArticle ? (
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                    >
                      {isMobile ? (
                        <div className={Styles.actionsButton}>
                          {file.type === 'article' && (
                            <IconButton
                              icon={MoreDots}
                              aria-label="File Menu"
                              onPress={() =>
                                drawersStore.openDrawer(DocMenuDrawer, {
                                  articleId: file.id,
                                  hiddenKeys: docMenuHiddenKeys,
                                })
                              }
                            />
                          )}
                          {file.type === 'folder' && (
                            <IconButton
                              icon={MoreDots}
                              aria-label="Project Menu"
                              onPress={() =>
                                drawersStore.openDrawer(ProjectMenuDrawer, {
                                  folderId: file.id,
                                })
                              }
                            />
                          )}
                        </div>
                      ) : (
                        <DropdownTrigger useSqueeze={!isMobileMd}>
                          <Tooltip label="Options" placement="top">
                            <div>
                              <NavItem.Control aria-label="Options" icon={MoreThick} />
                            </div>
                          </Tooltip>
                          {(close) => {
                            if (isFolder) return folderMenuPopover(close)
                            if (isArticle) return docMenuPopover(close)
                          }}
                        </DropdownTrigger>
                      )}
                    </div>
                  ) : (
                    <Tooltip label="Options" placement="top">
                      <div>
                        <NavItem.Control icon={MoreThick} onPress={handleRightClick} aria-label="Open menu" />
                      </div>
                    </Tooltip>
                  )}
                </NavItem.ControlGroup>
              </NavItem.Root>
            </ConditionalWrapper>
          </ConditionalWrapper>
        )}
      </div>
      {isLast && (
        <div style={{ visibility: 'hidden' }}>
          {isRootLevelLeaf && loadMore && <InfiniteScroll hasMore={!!hasMore} loadMore={loadMore} />}
          {!isRootLevelLeaf && (
            <InfiniteScroll hasMore={!!file.parentClient?.hasMore} loadMore={file.parentClient?.loadMore} />
          )}
        </div>
      )}
    </>
  )
})
